import React from "react";
import { Typography, Link, Stack, Paper, Box } from "@mui/material";
import Divider from "@mui/material/Divider";

const SDLComponent = ({ activeTheme, infoCardStyle }) => {
    return (
        <Box sx={{ px: 4, py: 3, maxWidth: 1400 }}>
            <Typography variant="h3" gutterBottom sx={{ mb: 2, fontWeight: 700 }}>
                SDL - Software Defined Logger
            </Typography>
            
            {/* Description */}
            <Paper elevation={0} sx={infoCardStyle}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Description
                </Typography>
                <Typography variant="body1" paragraph>
                    How to construct API queries for retrieving Files, DTC Logs, part number data, and Test result ingestion data.
                </Typography>
            </Paper>

            {/* Base URL */}
            <Paper elevation={0} sx={infoCardStyle}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Base URL
                </Typography>
                <Box sx={{ 
                    bgcolor: activeTheme.palette.mode === "dark" ? "#1e1e1e" : "#f5f5f5",
                    p: 2,
                    borderRadius: 1,
                    fontFamily: 'monospace'
                }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        https://sdl-api.musea2.azure.ext.gm.com/
                    </Typography>
                </Box>
            </Paper>

            {/* Authentication */}
            <Paper elevation={0} sx={infoCardStyle}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Authentication
                </Typography>
                <Typography variant="body2" paragraph>
                    Include your API key in the request headers:
                </Typography>
                <Box sx={{ 
                    bgcolor: activeTheme.palette.mode === "dark" ? "#1e1e1e" : "#f5f5f5",
                    p: 2,
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    overflowX: 'auto'
                }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        curl -X GET "https://sdl-api.musea2.azure.ext.gm.com/api/v1/dtc_logs?start_date=2025-04-01T00:00:00&end_date=2025-04-02T00:00:00" -H "Authorization: Api-Key {'{YOUR_API_KEY}'}" -H "Content-Type: application/json"
                    </Typography>
                </Box>
            </Paper>

            {/* Common Parameters */}
            <Paper elevation={0} sx={infoCardStyle}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Common Parameters
                </Typography>
                <Stack spacing={2}>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Date Format
                        </Typography>
                        <Typography variant="body2">
                            All date parameters must follow ISO 8601 format:
                        </Typography>
                        <Typography variant="body2">• Format: YYYY-MM-DDTHH:MM:SSZ (UTC)</Typography>
                        <Typography variant="body2">• Example: 2024-03-01T00:00:00Z</Typography>
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Multiple Values
                        </Typography>
                        <Typography variant="body2">
                            Multiple values can be passed as comma-separated strings:
                        </Typography>
                        <Typography variant="body2">• vin=VIN1,VIN2,VIN3</Typography>
                        <Typography variant="body2">• mac_id=MAC1,MAC2</Typography>
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Query Options
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Option 1: Date Range Query</Typography>
                        <Typography variant="body2">• Required: start_date AND end_date</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>Option 2: File-based Query</Typography>
                        <Typography variant="body2">• Required: Choose one of: filename OR file_location</Typography>
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Standard Parameters
                        </Typography>
                        <Typography variant="body2">• vin - Vehicle Identification Number</Typography>
                        <Typography variant="body2">• mac_id - MAC Address Identifier</Typography>
                        <Typography variant="body2">• did - Device Identifier</Typography>
                        <Typography variant="body2">• module - Module specification</Typography>
                        <Typography variant="body2">• start_date - Start date in ISO format (Conditional)</Typography>
                        <Typography variant="body2">• end_date - End date in ISO format (Conditional)</Typography>
                        <Typography variant="body2">• response - Response format specification</Typography>
                        <Typography variant="body2">• filename - Target filename (Conditional)</Typography>
                        <Typography variant="body2">• file_location - File path or location (Conditional)</Typography>
                    </Box>
                </Stack>
            </Paper>

            {/* API Endpoints */}
            <Paper elevation={0} sx={infoCardStyle}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                    API Endpoints
                </Typography>
                
                {/* DTC Logs */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        1. DTC Logs
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'primary.main', mb: 1 }}>
                        GET /api/v1/dtc_logs
                    </Typography>
                    <Typography variant="body2" paragraph>
                        Retrieve Diagnostic Trouble Code (DTC) logs from vehicle systems.
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Additional Parameters:</Typography>
                    <Typography variant="body2">• ecu_code - Electronic Control Unit code</Typography>
                    <Typography variant="body2">• dtc_code - Diagnostic Trouble Code</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1 }}>Example Request:</Typography>
                    <Box sx={{ 
                        bgcolor: activeTheme.palette.mode === "dark" ? "#1e1e1e" : "#f5f5f5",
                        p: 1.5,
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        mt: 1
                    }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                            GET /api/v1/dtc_logs?start_date=2024-03-01T00:00:00Z&end_date=2024-03-31T23:59:59Z&module=Transmission
                        </Typography>
                    </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Events Query */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        2. Events Query
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'primary.main', mb: 1 }}>
                        GET /api/v1/events/query
                    </Typography>
                    <Typography variant="body2" paragraph>
                        Query vehicle event logs and triggers.
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Additional Parameters:</Typography>
                    <Typography variant="body2">• trip - Trip identifier</Typography>
                    <Typography variant="body2">• summary - Event summary (contains search)</Typography>
                    <Typography variant="body2">• trigger_type - Type of trigger event</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1 }}>Example Request:</Typography>
                    <Box sx={{ 
                        bgcolor: activeTheme.palette.mode === "dark" ? "#1e1e1e" : "#f5f5f5",
                        p: 1.5,
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        mt: 1
                    }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                            GET /api/v1/events/query?vin=1GYS9GRL8SR100019&trip=724&trigger_type=trigger
                        </Typography>
                    </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Loggers */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        3. Loggers
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'primary.main', mb: 1 }}>
                        GET /api/v1/loggers
                    </Typography>
                    <Typography variant="body2" paragraph>
                        Retrieve logger file metadata with pagination support.
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Additional Parameters:</Typography>
                    <Typography variant="body2">• file_type - File type filter (.csv, .txt, etc.)</Typography>
                    <Typography variant="body2">• category - File category filter</Typography>
                    <Typography variant="body2">• page_size - Results per page (max 1000, default 100)</Typography>
                    <Typography variant="body2">• page - Page number for pagination (default 1)</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1 }}>Example Request:</Typography>
                    <Box sx={{ 
                        bgcolor: activeTheme.palette.mode === "dark" ? "#1e1e1e" : "#f5f5f5",
                        p: 1.5,
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        mt: 1
                    }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                            GET /api/v1/loggers?start_date=2024-01-01T00:00:00Z&end_date=2024-01-31T23:59:59Z&page_size=100&page=1
                        </Typography>
                    </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Part Number Pull */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        4. Part Number Pull
                    </Typography>
                    <Typography variant="body2" paragraph>
                        Query part number pull data including diagnostic IDs, modules, responses, and DIDs from vehicle systems.
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Endpoints:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'primary.main' }}>
                        GET /api/v1/part_number_pulls/query
                    </Typography>
                    <Typography variant="body2" color="text.secondary">V1 - filename pagination</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'primary.main', mt: 1 }}>
                        GET /api/v2/part_number_pulls/query
                    </Typography>
                    <Typography variant="body2" color="text.secondary">V2 - optimized with caching (recommended)</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'primary.main', mt: 1 }}>
                        GET /api/v1/part_number_pulls/by_filename
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Get records by file location</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>V2 Additional Parameters:</Typography>
                    <Typography variant="body2">• diagnostic_id - Diagnostic identifier(s), comma-separated</Typography>
                    <Typography variant="body2">• page_size - Results per page (1-100, default: 10)</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1 }}>Example Request:</Typography>
                    <Box sx={{ 
                        bgcolor: activeTheme.palette.mode === "dark" ? "#1e1e1e" : "#f5f5f5",
                        p: 1.5,
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        mt: 1
                    }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                            GET /api/v2/part_number_pulls/query?start_date=2024-03-01T00:00:00Z&end_date=2024-03-31T23:59:59Z&vin=1HGCM82633A123456
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Search Behavior */}
            <Paper elevation={0} sx={infoCardStyle}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Search Behavior
                </Typography>
                <Typography variant="body2">• Events API: The summary parameter uses contains matching (partial string search)</Typography>
                <Typography variant="body2">• Other Parameters: Use exact matching unless otherwise specified</Typography>
                <Typography variant="body2">• Multiple Values: Comma-separated values are treated as OR conditions</Typography>
            </Paper>

            {/* Error Handling */}
            <Paper elevation={0} sx={infoCardStyle}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Error Handling
                </Typography>
                <Typography variant="body2" paragraph>
                    The API returns standard HTTP status codes with descriptive error messages:
                </Typography>
                <Typography variant="body2">• 200 - Success</Typography>
                <Typography variant="body2">• 400 - Bad Request - Invalid parameters or missing required fields</Typography>
                <Typography variant="body2">• 401 - Unauthorized - Invalid or missing API key</Typography>
                <Typography variant="body2">• 404 - Not Found - Endpoint or resource not found</Typography>
                <Typography variant="body2">• 429 - Too Many Requests - Rate limit exceeded</Typography>
                <Typography variant="body2">• 500 - Internal Server Error - Server-side error</Typography>
            </Paper>

            {/* Local Development Setup */}
            <Paper elevation={0} sx={infoCardStyle}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Local Development Setup
                </Typography>
                
                <Stack spacing={2}>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Prerequisites
                        </Typography>
                        <Typography variant="body2">• Python 3.10 or higher</Typography>
                        <Typography variant="body2">• PostgreSQL 13 or higher</Typography>
                        <Typography variant="body2">• Redis (for Celery task queue)</Typography>
                        <Typography variant="body2">• Git</Typography>
                    </Box>

                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Installation Steps
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>1. Clone the Repository</Typography>
                        <Box sx={{ 
                            bgcolor: activeTheme.palette.mode === "dark" ? "#1e1e1e" : "#f5f5f5",
                            p: 1.5,
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            mt: 1,
                            mb: 1
                        }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                git clone https://github.com/GM-InnerSource/SDL_Backend.git<br/>
                                cd SDL_Backend/backend
                            </Typography>
                        </Box>

                        <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>2. Create Virtual Environment</Typography>
                        <Typography variant="body2" color="text.secondary">Windows:</Typography>
                        <Box sx={{ 
                            bgcolor: activeTheme.palette.mode === "dark" ? "#1e1e1e" : "#f5f5f5",
                            p: 1.5,
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            mt: 0.5,
                            mb: 1
                        }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                python -m venv .venv<br/>
                                .\.venv\Scripts\activate
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">Linux/Mac:</Typography>
                        <Box sx={{ 
                            bgcolor: activeTheme.palette.mode === "dark" ? "#1e1e1e" : "#f5f5f5",
                            p: 1.5,
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            mt: 0.5,
                            mb: 1
                        }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                python3 -m venv .venv<br/>
                                source .venv/bin/activate
                            </Typography>
                        </Box>

                        <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>3. Install Dependencies</Typography>
                        <Box sx={{ 
                            bgcolor: activeTheme.palette.mode === "dark" ? "#1e1e1e" : "#f5f5f5",
                            p: 1.5,
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            mt: 0.5,
                            mb: 1
                        }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                pip install -r requirements/local.txt
                            </Typography>
                        </Box>

                        <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>4. Database Setup</Typography>
                        <Typography variant="body2">Using Docker Compose (Recommended):</Typography>
                        <Box sx={{ 
                            bgcolor: activeTheme.palette.mode === "dark" ? "#1e1e1e" : "#f5f5f5",
                            p: 1.5,
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            mt: 0.5,
                            mb: 1
                        }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                docker-compose up -d db redis
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            This starts PostgreSQL on port 5432 and Redis on port 6379
                        </Typography>

                        <Typography variant="body2" sx={{ fontWeight: 600, mt: 2 }}>5. Run Migrations</Typography>
                        <Box sx={{ 
                            bgcolor: activeTheme.palette.mode === "dark" ? "#1e1e1e" : "#f5f5f5",
                            p: 1.5,
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            mt: 0.5,
                            mb: 1
                        }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                python manage.py migrate
                            </Typography>
                        </Box>

                        <Typography variant="body2" sx={{ fontWeight: 600, mt: 2 }}>6. Run Development Server</Typography>
                        <Box sx={{ 
                            bgcolor: activeTheme.palette.mode === "dark" ? "#1e1e1e" : "#f5f5f5",
                            p: 1.5,
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            mt: 0.5,
                            mb: 1
                        }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                python manage.py runserver
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            Server will start on http://127.0.0.1:8000/
                        </Typography>

                        <Typography variant="body2" sx={{ fontWeight: 600, mt: 2 }}>7. Run Celery Worker (Optional)</Typography>
                        <Box sx={{ 
                            bgcolor: activeTheme.palette.mode === "dark" ? "#1e1e1e" : "#f5f5f5",
                            p: 1.5,
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            mt: 0.5
                        }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                celery -A coreApi worker --loglevel=info
                            </Typography>
                        </Box>
                    </Box>
                </Stack>
            </Paper>

            {/* Database Schema */}
            <Paper elevation={0} sx={infoCardStyle}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Database Schema
                </Typography>
                <Typography variant="body2" paragraph>
                    The SDL Backend uses PostgreSQL with several main areas:
                </Typography>
                <Stack spacing={1.5}>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Core Models
                        </Typography>
                        <Typography variant="body2">• Region - Geographic regions for data segregation</Typography>
                        <Typography variant="body2">• Device - Connected devices by MAC address</Typography>
                        <Typography variant="body2">• Vehicle - Vehicles identified by VIN</Typography>
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Data Models
                        </Typography>
                        <Typography variant="body2">• Logger - Central logging for file uploads</Typography>
                        <Typography variant="body2">• DTCLog - Diagnostic Trouble Code logs</Typography>
                        <Typography variant="body2">• Event - Event logs and triggers</Typography>
                        <Typography variant="body2">• PartNumberPull - Part number data</Typography>
                        <Typography variant="body2">• DIT - Device Identification Table</Typography>
                        <Typography variant="body2">• TestResultIngestion - Test results</Typography>
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Lookup Tables
                        </Typography>
                        <Typography variant="body2">• DTCMapping, ECUMapping - Code mappings</Typography>
                        <Typography variant="body2">• Field Lookups - Autocomplete optimization</Typography>
                        <Typography variant="body2">• Module Response Lookups - Fast queries</Typography>
                    </Box>
                </Stack>
            </Paper>

            {/* Data Transformation */}
            <Paper elevation={0} sx={infoCardStyle}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Data Transformation Functions
                </Typography>
                <Typography variant="body2" paragraph>
                    Comprehensive data transformation capabilities with two key variables:
                </Typography>
                <Stack spacing={2}>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Content Variables
                        </Typography>
                        <Typography variant="body2">• blob_contents - Raw, unmodified file contents</Typography>
                        <Typography variant="body2">• blob_contents_format - Processed and parsed version ready for database</Typography>
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Supported Data Types
                        </Typography>
                        <Typography variant="body2">1. Part Number Pulls (PNPDT)</Typography>
                        <Typography variant="body2">2. DTC Logs (DTCDT)</Typography>
                        <Typography variant="body2">3. Event Logs (eventDT)</Typography>
                        <Typography variant="body2">4. Test Result Ingestions (TRIDT)</Typography>
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Usage Example
                        </Typography>
                        <Box sx={{ 
                            bgcolor: activeTheme.palette.mode === "dark" ? "#1e1e1e" : "#f5f5f5",
                            p: 1.5,
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            mt: 1
                        }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                from part_number_pull.data_transformation import DataTransformation as PNPDT<br/>
                                <br/>
                                transformer = PNPDT(file_location)<br/>
                                transformer.process()  # Process file<br/>
                                transformer.full_process()  # Process and push to DB
                            </Typography>
                        </Box>
                    </Box>
                </Stack>
            </Paper>

            {/* Documentation Links */}
            <Paper elevation={0} sx={infoCardStyle}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Documentation & Resources
                </Typography>
                <Stack spacing={1}>
                    <Link href="https://github.com/GM-InnerSource/SDL_Backend" target="_blank" rel="noopener noreferrer" underline="hover">
                        SDL Backend Repository (GitHub)
                    </Link>
                    <Link href="https://github.com/GM-InnerSource/SDL_Frontend" target="_blank" rel="noopener noreferrer" underline="hover">
                        SDL Frontend Repository (GitHub)
                    </Link>
                </Stack>
            </Paper>

            {/* Support & Contact */}
            <Paper elevation={0} sx={infoCardStyle}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Support & Contact
                </Typography>
                <Typography variant="body2" paragraph>
                    For API support and questions:
                </Typography>
                <Stack spacing={0.5} sx={{ pl: 2 }}>
                    <Typography variant="body2">• Ryan Abukarn (ryan.abukarn@gm.com)</Typography>
                    <Typography variant="body2">• Carmina Echipare (carmina.echipare@gm.com)</Typography>
                </Stack>
            </Paper>
        </Box>
    );
};

export default SDLComponent;
